import React, { useState, useEffect, useRef } from 'react';
import { Check, Users, Package, User, ChevronRight, ChevronLeft, Minus, Plus, Download, Info, MapPin } from 'lucide-react';
import html2canvas from 'html2canvas';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png";

const DEFAULT_PACKAGES = [
  { type: 'solo_ingresso', label: 'Solo Ingresso', icon: '🎟️', price: 10 },
  { type: 'ingresso_guardaroba', label: 'Ingresso + Guardaroba', icon: '🧥', price: 15 },
  { type: 'ingresso_solo_tavolo', label: 'Ingresso solo Tavolo', icon: '🪑', price: 12, displayPrice: 'Gratuito con l\'ingresso', subtitle: 'salvo disponibilità' },
  { type: 'ingresso_tavolo_bottiglia', label: 'Ingresso + Tavolo e Bottiglia', icon: '🍾', price: 40 },
  { type: 'ingresso_tavolo_bottiglia_guardaroba', label: 'Ingresso + Tavolo, Bottiglia e Guardaroba', icon: '⭐', price: 50 },
];

function getEventPackages(event) {
  if (event?.packages && event.packages.length > 0) {
    // Filter out disabled packages
    const pkgs = event.packages.filter(p => p.enabled !== false);
    // Ensure "ingresso_solo_tavolo" is always present (if not explicitly disabled)
    const wasDisabled = event.packages.some(p => p.type === 'ingresso_solo_tavolo' && p.enabled === false);
    if (!wasDisabled && !pkgs.find(p => p.type === 'ingresso_solo_tavolo')) {
      const defaultPkg = DEFAULT_PACKAGES.find(p => p.type === 'ingresso_solo_tavolo');
      const insertIdx = pkgs.findIndex(p => p.type === 'ingresso_guardaroba');
      pkgs.splice(insertIdx >= 0 ? insertIdx + 1 : 2, 0, { ...defaultPkg });
    }
    // Enrich ingresso_solo_tavolo with display info
    return pkgs.map(p => p.type === 'ingresso_solo_tavolo'
      ? { ...p, displayPrice: 'Gratuito con l\'ingresso', subtitle: 'salvo disponibilità' }
      : p
    );
  }
  return DEFAULT_PACKAGES;
}

const STEPS = ['Pacchetto', 'Persone', 'Dati', 'Conferma'];

const TABLE_POSITIONS = [
  { id: 'laterale_destra', label: 'Laterale a destra' },
  { id: 'laterale_sinistra', label: 'Laterale a sinistra' },
  { id: 'fronte_orchestra', label: 'Fronte orchestra' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.split(' ')[0]);
    return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}

export default function BookingWizard({ events, onEventDetail, ctaLabel, headerEyebrow, headerTitle, headerSubtitle, accent, clubVariant }) {
  const isJimmys = clubVariant === 'jimmys';
  const [step, setStep]               = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const hasTavolo = selectedPkg?.type?.includes('tavolo');
  const [numPeople, setNumPeople]     = useState(2);
  const [form, setForm]               = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [tablePosition, setTablePosition] = useState('');
  const [extras, setExtras]           = useState({ bottiglia: false, bottiglia_nome: '', torta: false, torta_testo: '', apericena: false });
  const [booking, setBooking]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const cardRef = useRef(null);
  const sectionRef = useRef(null);

  const prevStepRef = useRef(step);
  useEffect(() => {
    if (prevStepRef.current !== step) {
      prevStepRef.current = step;
      const targetId = step === 2 ? 'step2-content' : step === 3 ? 'step3-content' : step === 4 ? 'step4-content' : null;
      if (targetId) {
        setTimeout(() => document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
      } else {
        setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }
    }
  }, [step]);

  // If an event was passed via URL hash (from event card CTA), pre-select it
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#prenota-')) {
      const eventId = hash.replace('#prenota-', '');
      const ev = events.find(e => e.id === eventId);
      if (ev) setSelectedEvent(ev);
    } else if (events.length === 1 && !selectedEvent) {
      // Auto-select if only one event (e.g. Jimmy's single-event page)
      setSelectedEvent(events[0]);
    }
  }, [events]);

  const canNext = () => {
    if (step === 1) return selectedEvent && selectedPkg;
    if (step === 2) return numPeople >= 1;
    if (step === 3) return form.first_name.trim() && form.last_name.trim() && form.phone.trim() && (!hasTavolo || tablePosition);
    return true;
  };

  const handleNext = () => { if (canNext()) setStep(s => s + 1); };
  const handleBack = () => { setStep(s => s - 1); setError(''); };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/bookings`, {
        event_id: selectedEvent.id,
        package_type: selectedPkg.type,
        package_label: selectedPkg.label,
        package_price: selectedPkg.price,
        num_people: numPeople,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        table_position: tablePosition || null,
        extras: {
          bottiglia: extras.bottiglia,
          bottiglia_nome: extras.bottiglia ? extras.bottiglia_nome : '',
          torta: extras.torta,
          torta_testo: extras.torta ? extras.torta_testo : '',
          apericena: extras.apericena,
        },
      });
      setBooking(res.data);
      setStep(4);
    } catch (e) {
      setError('Si è verificato un errore. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, { backgroundColor: '#0d1b2a', scale: 2, useCORS: true });
    const link = document.createElement('a');
    link.download = `prenotazione-${booking?.booking_code}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const handleNewBooking = () => {
    setStep(1); setSelectedEvent(null); setSelectedPkg(null);
    setNumPeople(2); setForm({ first_name: '', last_name: '', email: '', phone: '' });
    setTablePosition(''); setExtras({ bottiglia: false, bottiglia_nome: '', torta: false, torta_testo: '', apericena: false });
    setBooking(null); setError('');
  };

  return (
    <section id="prenota" ref={sectionRef} data-testid="booking-section" className={`py-24 relative overflow-hidden ${isJimmys ? 'jimmys-theme' : ''}`} style={{ background: 'var(--bg-secondary)' }}>
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {/* large soft orb top-right */}
        <div style={{
          position: 'absolute', top: '-10%', right: '-5%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: isJimmys
            ? 'radial-gradient(circle, rgba(236,72,153,0.10) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)',
        }} />
        {/* orb bottom-left */}
        <div style={{
          position: 'absolute', bottom: '-10%', left: '-5%',
          width: '400px', height: '400px', borderRadius: '50%',
          background: isJimmys
            ? 'radial-gradient(circle, rgba(168,85,247,0.10) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(79,195,212,0.05) 0%, transparent 70%)',
        }} />
        {/* subtle grid lines */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: isJimmys
            ? 'linear-gradient(rgba(236,72,153,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(236,72,153,0.04) 1px, transparent 1px)'
            : 'linear-gradient(rgba(201,168,76,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>
      <div className="max-w-4xl mx-auto px-4 md:px-8 relative" style={{ zIndex: 1 }}>
        {/* Header - hidden on step 4 */}
        {step < 4 && (
          <div className="text-center mb-14">
            <p className="section-eyebrow">{headerEyebrow || 'Sistema di Prenotazione'}</p>
            <h2 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-5">
              {headerTitle || 'Prenota il tuo posto'}
            </h2>
            <p className="text-gray-400 text-xl max-w-xl mx-auto">
              {headerSubtitle || "Scegli il pacchetto e assicurati il tuo posto. Il pagamento avviene all'ingresso."}
            </p>
          </div>
        )}

        {/* Step Indicator */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-0 mb-12" data-testid="step-indicator">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const status = n < step ? 'done' : n === step ? 'active' : 'idle';
              return (
                <React.Fragment key={n}>
                  <div className="flex flex-col items-center gap-2">
                    <div className={`step-dot ${status}`} style={{ width: 38, height: 38, fontSize: '0.85rem' }}>
                      {status === 'done' ? <Check size={18} /> : n}
                    </div>
                    <span className={`text-sm font-manrope font-600 hidden sm:block ${status === 'active' ? 'text-gold' : 'text-gray-500'}`}>
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px w-8 sm:w-12 md:w-20 mx-1 sm:mx-2 mb-7 sm:mb-7 transition-all ${n < step ? 'bg-gold' : 'bg-white/10'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Progress bar */}
        {step < 4 && (
          <div className="progress-bar mb-10">
            <div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }} />
          </div>
        )}

        {/* ── STEP 1: Evento + Pacchetto (pacchetti appaiono dopo selezione serata) ── */}
        {step === 1 && (
          <div data-testid="step-1" className="animate-fade-in">
            {/* Payment banner */}
            <div
              className="flex items-center gap-4 p-5 rounded-2xl mb-10"
              data-testid="payment-banner-booking"
              style={isJimmys ? {
                background: 'linear-gradient(135deg, rgba(236,72,153,0.14), rgba(168,85,247,0.08))',
                border: '2px solid rgba(236,72,153,0.4)',
                boxShadow: '0 0 30px rgba(236,72,153,0.15)',
              } : {
                background: 'linear-gradient(135deg, rgba(201,168,76,0.12), rgba(201,168,76,0.06))',
                border: '2px solid rgba(201,168,76,0.4)',
                boxShadow: '0 0 30px rgba(201,168,76,0.1)',
              }}
            >
              <div className="text-3xl shrink-0">💵</div>
              <div>
                <p className="font-playfair text-xl font-bold text-gold">PAGAMENTO SOLO IN CASSA</p>
                <p className="text-gray-300 text-base mt-1">
                  La prenotazione online è gratuita. Il pagamento avviene esclusivamente all'ingresso della serata.
                </p>
              </div>
            </div>

            <h3 className="font-playfair text-3xl font-bold text-white mb-6">
              Seleziona la serata
            </h3>
            {events.length === 0 ? (
              <p className="text-gray-400 text-xl">Nessun evento disponibile al momento.</p>
            ) : (
              <div className="space-y-4 mb-10">
                {events.map(ev => (
                  <div key={ev.id}>
                    <button
                      data-testid={`event-select-${ev.id}`}
                      onClick={() => {
                        if (ev.sold_out) return;
                        setSelectedEvent(ev); setSelectedPkg(null);
                        setTimeout(() => document.getElementById(`packages-${ev.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                      }}
                      className={`card-booking text-left transition-all w-full ${selectedEvent?.id === ev.id ? 'selected' : ''} ${ev.sold_out ? 'opacity-60 cursor-not-allowed' : ''}`}
                      style={{ padding: 0, overflow: 'hidden' }}
                    >
                      <div className="flex items-stretch gap-0">
                        <div className="shrink-0 relative overflow-hidden" style={{ minWidth: '110px', width: '110px', height: '110px' }}>
                          <img
                            src={ev.image_url || "/api/uploads/logo_fallback.png"}
                            alt={ev.title}
                            className="w-full h-full"
                            style={ev.image_url ? { objectFit: 'cover' } : { objectFit: 'contain', padding: '12px', background: '#0a0a0f' }}
                            onError={e => { e.target.style.objectFit='contain'; e.target.style.padding='12px'; e.target.style.background='#0a0a0f'; e.target.src="/api/uploads/logo_fallback.png"; }}
                          />
                          {selectedEvent?.id === ev.id && (
                            <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.35)' }}>
                              <Check size={28} className="text-white" strokeWidth={3} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 p-4 flex items-center justify-between">
                          <div>
                            <p className="font-playfair font-bold text-white text-lg leading-tight">{ev.title}</p>
                            <p className="text-gold text-sm font-manrope font-600 mt-1.5">
                              {formatDate(ev.date)} &bull; ore {ev.time}
                            </p>
                            {ev.special_guest && (
                              <p className="text-sm mt-1 font-600" style={{ color: 'var(--color-gold)' }}>★ {ev.special_guest}</p>
                            )}
                            {ev.tickets_low && !ev.sold_out && (
                              <p className="text-xs mt-1 font-700" style={{ color: '#f87171', animation: 'pulse 2s ease-in-out infinite' }}>🔥 Biglietti in esaurimento</p>
                            )}
                            {ev.sold_out && (
                              <span className="inline-block mt-1.5 px-3 py-1 rounded-full text-xs font-manrope font-800 uppercase tracking-wider" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }} data-testid={`sold-out-${ev.id}`}>
                                SOLD OUT
                              </span>
                            )}
                          </div>
                          {onEventDetail && (
                            <button
                              data-testid={`info-booking-${ev.id}`}
                              onClick={(e) => { e.stopPropagation(); onEventDetail(ev); }}
                              className="shrink-0 ml-3 w-10 h-10 rounded-full flex items-center justify-center transition-all"
                              style={{ border: '1px solid rgba(201,168,76,0.4)', color: 'var(--color-gold)' }}
                            >
                              <Info size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Packages appear below selected event - 2x2 grid */}
                    {selectedEvent?.id === ev.id && (
                      <div id={`packages-${ev.id}`} className="mt-3 animate-fade-in">
                        <p className="text-gold font-manrope font-700 text-sm uppercase tracking-wider mb-3">Scegli il pacchetto</p>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {getEventPackages(ev).map((pkg, idx, arr) => (
                            <button
                              key={pkg.type}
                              data-testid={`pkg-${pkg.type}`}
                              onClick={() => {
                                setSelectedPkg(pkg);
                                setTimeout(() => document.getElementById('step1-confirm-area')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                              }}
                              className={`card-booking text-left ${selectedPkg?.type === pkg.type ? 'selected' : ''}`}
                              style={{ padding: '1rem', gridColumn: (arr.length % 2 !== 0 && idx === arr.length - 1) ? '1 / -1' : undefined }}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span style={{ fontSize: '1.5rem' }}>{pkg.icon}</span>
                                {pkg.displayPrice
                                  ? <span className="font-manrope font-bold text-gold text-xs text-right leading-tight" style={{ maxWidth: '60%' }}>{pkg.displayPrice}</span>
                                  : <span className="font-playfair font-bold text-gold text-lg">&euro; {pkg.price}</span>
                                }
                              </div>
                              <p className="font-playfair font-bold text-white text-base leading-tight">{pkg.label}</p>
                              {pkg.subtitle && <p className="text-gray-500 font-manrope text-xs mt-0.5 italic">{pkg.subtitle}</p>}
                              {selectedPkg?.type === pkg.type && (
                                <div className="mt-1.5 flex items-center gap-1.5 text-gold text-xs font-600">
                                  <Check size={14} /> SELEZIONATO
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div id="step1-confirm-area">
              <button
                data-testid="step1-next"
                onClick={handleNext}
                disabled={!canNext()}
                className={`btn-primary w-full justify-center flex items-center gap-2 transition-all text-base sm:text-lg ${!canNext() ? 'opacity-30 cursor-not-allowed scale-95' : 'animate-fade-in'}`}
                style={{ padding: '1rem 1.5rem' }}
              >
                CONTINUA <ChevronRight size={22} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Numero persone ── */}
        {step === 2 && (
          <div data-testid="step-2" className="animate-fade-in">
            <div id="step2-content">
              <h3 className="font-playfair text-3xl font-bold text-white mb-2">Quante persone?</h3>
              <p className="text-gray-400 text-xl mb-10">Seleziona il numero di persone per la prenotazione.</p>
            </div>

            <div className="card-dark p-6 sm:p-10 flex flex-col items-center gap-6 sm:gap-8">
              <p className="font-playfair text-xl sm:text-2xl text-white font-bold text-center">{selectedPkg?.label}</p>
              <div className="flex items-center gap-4 sm:gap-10">
                <button
                  data-testid="people-minus"
                  onClick={() => setNumPeople(n => Math.max(1, n - 1))}
                  className="w-14 h-14 sm:w-20 sm:h-20 rounded-full border-2 border-gold text-gold flex items-center justify-center hover:bg-gold/10 transition-all shrink-0"
                >
                  <Minus size={24} />
                </button>
                <span data-testid="people-count" className="font-playfair font-bold text-white text-center" style={{ fontSize: 'clamp(3.5rem, 10vw, 6rem)', lineHeight: 1, minWidth: '80px' }}>
                  {numPeople}
                </span>
                <button
                  data-testid="people-plus"
                  onClick={() => setNumPeople(n => Math.min(20, n + 1))}
                  className="w-14 h-14 sm:w-20 sm:h-20 rounded-full border-2 border-gold text-gold flex items-center justify-center hover:bg-gold/10 transition-all shrink-0"
                >
                  <Plus size={24} />
                </button>
              </div>
              <p className="text-gray-400 text-xl flex items-center gap-2">
                <Users size={24} className="text-gold" />
                {numPeople === 1 ? '1 persona' : `${numPeople} persone`}
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button data-testid="step2-back" onClick={handleBack} className="btn-secondary flex-1 justify-center flex items-center gap-2 text-sm sm:text-base">
                <ChevronLeft size={18} /> INDIETRO
              </button>
              <button data-testid="step2-next" onClick={handleNext} className="btn-primary flex-1 justify-center flex items-center gap-2 text-sm sm:text-base" style={{ padding: '1rem 1rem' }}>
                CONTINUA <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Dati personali + Info aggiuntive ── */}
        {step === 3 && (
          <div data-testid="step-3" className="animate-fade-in">
            <div id="step3-content">
              <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-white mb-2">I tuoi dati</h3>
              <p className="text-gray-400 text-base sm:text-xl mb-8">Inserisci nome, cognome e telefono per la prenotazione.</p>
            </div>

            <div className="card-dark p-5 sm:p-8 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-manrope font-600 text-gray-200 mb-2">Nome *</label>
                  <input data-testid="input-first-name" type="text" placeholder="Il tuo nome"
                    value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    className="input-elegant" style={{ fontSize: '1.2rem', height: '64px' }} />
                </div>
                <div>
                  <label className="block text-lg font-manrope font-600 text-gray-200 mb-2">Cognome *</label>
                  <input data-testid="input-last-name" type="text" placeholder="Il tuo cognome"
                    value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    className="input-elegant" style={{ fontSize: '1.2rem', height: '64px' }} />
                </div>
              </div>
              <div>
                <label className="block text-lg font-manrope font-600 text-gray-200 mb-2">Telefono *</label>
                <input data-testid="input-phone" type="tel" placeholder="351 000 0000"
                  value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="input-elegant" style={{ fontSize: '1.2rem', height: '64px' }} />
              </div>
            </div>

            {/* Posizione tavolo - solo per pacchetti con tavolo */}
            {hasTavolo && (
            <>
            <div className="card-dark p-8 mt-6">
              <p className="font-playfair text-xl font-bold text-white mb-4">Posizione tavolo <span className="text-red-400">*</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {TABLE_POSITIONS.map(pos => {
                  const disabled = selectedEvent?.disabled_seats?.includes(pos.id);
                  return (
                    <button
                      key={pos.id}
                      data-testid={`seat-${pos.id}`}
                      disabled={disabled}
                      onClick={() => setTablePosition(tablePosition === pos.id ? '' : pos.id)}
                      className={`card-booking text-center py-4 transition-all ${tablePosition === pos.id ? 'selected' : ''} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
                    >
                      <p className="font-manrope font-600 text-white text-base">{pos.label}</p>
                      {disabled && <p className="text-red-400 text-xs mt-1 uppercase">Esaurito</p>}
                      {tablePosition === pos.id && <p className="text-gold text-xs mt-1"><Check size={14} className="inline" /> Selezionato</p>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 px-4 py-3 rounded-xl flex items-start gap-3" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }} data-testid="table-placement-info">
              <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
              <p className="text-gray-300 font-manrope text-sm leading-relaxed">
                Il tavolo verr&agrave; assegnato nella <span className="text-gold font-600">miglior posizione disponibile</span> al momento della prenotazione, in base alla zona selezionata.
              </p>
            </div>
            </>
            )}

            {/* Info aggiuntive - solo per pacchetti con tavolo */}
            {hasTavolo && (
            <div className="card-dark p-8 mt-6">
              <p className="font-playfair text-xl font-bold text-white mb-4">Info aggiuntive</p>
              <div className="space-y-5">
                {/* Bottiglie particolari */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="extra-bottiglia" checked={extras.bottiglia}
                      onChange={e => setExtras(x => ({ ...x, bottiglia: e.target.checked }))}
                      className="w-6 h-6 rounded accent-gold" style={{ accentColor: 'var(--color-gold)' }} />
                    <span className="font-manrope font-600 text-white text-lg">Bottiglie particolari</span>
                  </label>
                  {extras.bottiglia && (
                    <input data-testid="extra-bottiglia-nome" type="text" placeholder="Nome della bottiglia..."
                      value={extras.bottiglia_nome} onChange={e => setExtras(x => ({ ...x, bottiglia_nome: e.target.value }))}
                      className="input-elegant mt-3" style={{ fontSize: '1.1rem', height: '56px' }} />
                  )}
                </div>
                {/* Torta compleanno */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="extra-torta" checked={extras.torta}
                      onChange={e => setExtras(x => ({ ...x, torta: e.target.checked }))}
                      className="w-6 h-6 rounded" style={{ accentColor: 'var(--color-gold)' }} />
                    <span className="font-manrope font-600 text-white text-lg">Torta di compleanno</span>
                  </label>
                  {extras.torta && (
                    <input data-testid="extra-torta-testo" type="text" placeholder="Testo per la torta (es. Buon compleanno Mario!)"
                      value={extras.torta_testo} onChange={e => setExtras(x => ({ ...x, torta_testo: e.target.value }))}
                      className="input-elegant mt-3" style={{ fontSize: '1.1rem', height: '56px' }} />
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Apericena con artista - box dorato indipendente - solo Peschiera */}
            {!isJimmys && (
            <div className="mt-6 p-6 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.12), rgba(201,168,76,0.05))', border: '1.5px solid rgba(201,168,76,0.35)' }} data-testid="apericena-section">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" data-testid="extra-apericena" checked={extras.apericena}
                  onChange={e => setExtras(x => ({ ...x, apericena: e.target.checked }))}
                  className="w-6 h-6 rounded" style={{ accentColor: 'var(--color-gold)' }} />
                <span className="font-playfair text-xl font-bold text-white">Apericena con artista</span>
                <span className="text-xs font-manrope px-2 py-0.5 rounded-full" style={{ background: 'rgba(201,168,76,0.2)', color: '#c9a84c' }}>servizio catering</span>
              </label>
              <p className="text-gold font-manrope font-bold text-lg mt-3">&euro; 20,00 a persona</p>
              <p className="text-gray-400 font-manrope text-sm mt-2">Per disponibilita' e menu' contattare direttamente il numero WhatsApp: <a href="https://wa.me/393518892962" target="_blank" rel="noopener noreferrer" className="text-gold underline">351 889 2962</a></p>
              {extras.apericena && (
                <p className="text-gold font-manrope text-sm mt-3 font-bold">+ &euro; {20 * numPeople} ({numPeople} x &euro; 20)</p>
              )}
            </div>
            )}

            {/* Riepilogo */}
            <div className="card-dark p-6 mt-6 border border-gold/20">
              <p className="text-gold font-600 font-manrope text-base mb-4 uppercase tracking-widest">Riepilogo prenotazione</p>
              <div className="space-y-3 text-gray-300 text-lg">
                <p><span className="text-white font-600">Serata:</span> {selectedEvent?.title}</p>
                <p><span className="text-white font-600">Data:</span> {formatDate(selectedEvent?.date)} ore {selectedEvent?.time}</p>
                <p><span className="text-white font-600">Pacchetto:</span> {selectedPkg?.label} — <span className="text-gold font-bold">&euro; {selectedPkg?.price} x {numPeople} = &euro; {(selectedPkg?.price || 0) * numPeople}</span></p>
                <p><span className="text-white font-600">Persone:</span> {numPeople}</p>
                {tablePosition && <p><span className="text-white font-600">Tavolo:</span> {TABLE_POSITIONS.find(p => p.id === tablePosition)?.label}</p>}
                {extras.bottiglia && <p><span className="text-white font-600">Bottiglia:</span> {extras.bottiglia_nome || 'Si'}</p>}
                {extras.torta && <p><span className="text-white font-600">Torta:</span> {extras.torta_testo || 'Si'}</p>}
                {extras.apericena && <p><span className="text-white font-600">Apericena:</span> &euro; 20 x {numPeople} = <span className="text-gold font-bold">&euro; {20 * numPeople}</span></p>}
              </div>
              <div className="mt-4 pt-4 border-t border-gold/20">
                <div className="flex justify-between items-center">
                  <span className="text-white font-playfair text-xl font-bold">Totale</span>
                  <span className="text-gold font-playfair text-2xl font-bold">&euro; {(selectedPkg?.price || 0) * numPeople + (extras.apericena ? 20 * numPeople : 0)}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3">
                <span className="text-2xl">💵</span>
                <p className="text-gray-400 text-sm">Pagamento in cassa all'ingresso. La prenotazione è gratuita.</p>
              </div>
            </div>

            {error && (
              <p data-testid="booking-error" className="text-red-400 mt-4 font-manrope">{error}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button data-testid="step3-back" onClick={handleBack} className="btn-secondary flex-1 justify-center flex items-center gap-2 text-sm sm:text-base">
                <ChevronLeft size={18} /> INDIETRO
              </button>
              <button
                data-testid="confirm-booking-btn"
                onClick={handleSubmit}
                disabled={loading || !canNext()}
                className={`btn-primary flex-1 justify-center flex items-center gap-2 text-sm sm:text-base ${(loading || !canNext()) ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{ padding: '1rem 1rem' }}
              >
                {loading ? 'INVIO...' : 'CONFERMA'} <Check size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: Conferma ── */}
        {step === 4 && booking && (
          <div id="step4-content" data-testid="step-4" className="animate-fade-in flex flex-col items-center">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-gold/20 border-2 border-gold flex items-center justify-center mx-auto mb-4 glow-gold">
                <Check size={40} className="text-gold" />
              </div>
              <h3 className="font-playfair text-3xl font-bold text-white mb-2">Prenotazione Confermata!</h3>
              <p className="text-gray-300 text-lg mb-1">Presenta questo biglietto alla cassa per il pagamento.</p>
              <p className="text-gold font-manrope font-700 text-base">Da pagare in cassa: &euro; {((booking.package_price || 0) * (booking.num_people || 1)) + (booking.extras?.apericena ? 20 * (booking.num_people || 1) : 0)}</p>
            </div>

            {/* Confirmation Card */}
            <div ref={cardRef} className="confirmation-card w-full max-w-md" data-testid="confirmation-card">
              <div className="text-center mb-6">
                <img
                  src={isJimmys ? "/api/uploads/jimmys_logo.png" : "/api/uploads/logo_peschiera.png"}
                  alt={isJimmys ? "Jimmy's Club" : "La Peschiera Club"}
                  className="h-16 object-contain mx-auto mb-3"
                  crossOrigin="anonymous"
                  data-testid="confirmation-logo"
                />
                <div className="neon-line mb-4" />
              </div>

              <div className="text-center mb-6">
                <p className="section-eyebrow">Codice Prenotazione</p>
                <p
                  data-testid="booking-code"
                  className="font-playfair text-3xl font-bold text-gold tracking-widest mt-1"
                >
                  {booking.booking_code}
                </p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-gray-400">Nominativo</span>
                  <span className="text-white font-600">{booking.first_name} {booking.last_name}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-gray-400">Serata</span>
                  <span className="text-white font-600 text-right max-w-40">{booking.event_title}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-gray-400">Data</span>
                  <span className="text-white font-600">{formatDate(booking.event_date)}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-gray-400">Pacchetto</span>
                  <span className="text-gold font-600 text-right max-w-44">{booking.package_label}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-gray-400">Dettaglio</span>
                  <span className="text-gray-300 text-sm">&euro; {booking.package_price || '—'} x {booking.num_people} pers.</span>
                </div>
                {booking.extras?.apericena && (
                  <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-gray-400">Apericena</span>
                    <span className="text-gold font-600">&euro; 20 x {booking.num_people} = &euro; {20 * (booking.num_people || 1)}</span>
                  </div>
                )}
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-gray-400">Totale</span>
                  <span className="text-gold font-bold text-lg">&euro; {((booking.package_price || 0) * (booking.num_people || 1)) + (booking.extras?.apericena ? 20 * (booking.num_people || 1) : 0)}</span>
                </div>
                <div className="flex justify-between pb-2">
                  <span className="text-gray-400">Persone</span>
                  <span className="text-white font-600">{booking.num_people}</span>
                </div>
              </div>

              <div className="divider-gold" />
              <p className="text-center text-aqua text-sm font-manrope font-600">
                Mostra questa conferma alla cassa per il pagamento
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button
                data-testid="download-btn"
                onClick={handleDownload}
                className="btn-secondary flex items-center gap-2"
              >
                <Download size={20} /> Scarica immagine
              </button>
              <button
                data-testid="new-booking-btn"
                onClick={handleNewBooking}
                className="btn-primary"
              >
                Nuova prenotazione
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
